<template>
  <div class="w-full h-full flex flex-row items-center gap-3">
    <div class="p-5 w-1/3 self-start flex flex-col gap-1 shadow rounded-lg">
      <span class="font-semibold">Sailing Time (Days)</span>
      <bar-chart
        v-if="sailingChartData"
        :key="chartHeight.sailing"
        class="w-full"
        :data="sailingChartData"
        :options="chartOptions"
        :plugins="chartPlugins"
        :height="chartHeight.sailing"
      />
      <span
        v-else
        class="h-16 text-grey-001"
      >
        No Data Found
      </span>
    </div>
    <div class="p-5 w-1/3 self-start flex flex-col gap-1 shadow rounded-lg">
      <span class="font-semibold">Fuel Time (Days)</span>
      <bar-chart
        v-if="fuelChartData"
        :key="chartHeight.fuel"
        class="w-full h-full"
        :data="fuelChartData"
        :options="chartOptions"
        :plugins="chartPlugins"
        :height="chartHeight.fuel"
      />
      <span
        v-else
        class="h-16 text-grey-001"
      >
        No Data Found
      </span>
    </div>
    <div class="w-1/3 h-full self-start flex flex-col items-center justify-start gap-3">
      <div class="p-3 w-full h-full flex flex-col gap-1 shadow rounded-lg">
        <span class="font-semibold">Loading Time (Days)</span>
        <bar-chart
          v-if="loadingTimeChartData"
          :key="chartHeight.loadingTime"
          class="w-full"
          :data="loadingTimeChartData"
          :options="stackedChartOptions"
          :plugins="chartPlugins"
          :height="chartHeight.loadingTime"
        />
        <span
          v-else
          class="h-16 text-grey-001"
        >
          No Data Found
        </span>
      </div>
      <div class="p-3 w-full h-full flex flex-col gap-1 shadow rounded-lg">
        <span class="font-semibold">Unloading Time (Days)</span>
        <bar-chart
          v-if="unloadingTimeChartData"
          :key="chartHeight.unloadingTime"
          class="w-full"
          :data="unloadingTimeChartData"
          :options="stackedChartOptions"
          :plugins="chartPlugins"
          :height="chartHeight.unloadingTime"
        />
        <span
          v-else
          class="h-16 text-grey-001"
        >
          No Data Found
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from "@/components/barChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  props: {
    data: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    BarChart
  },
  computed: {
    sailingData() {
      if (this.data) {
        return this.data.sailing_time
      }
      return null
    },
    fuelData() {
      if (this.data) {
        return this.data.fuel_time
      }
      return null
    },
    loadingTimeData() {
      if (this.data) {
        return this.data.loading_time
      }
      return null
    },
    unloadingTimeData() {
      if (this.data) {
        return this.data.unloading_time
      }
      return null
    },
    sailingChartData() {
      if (this.sailingData) {
        if (this.sailingData.every((item) => item.value === 0)) {
          return null
        }

        const baseline = this.sailingData[0].target
        return {
          labels: this.sailingData.map((item) => item.owner_name),
          datasets: [
            {
              type: 'line',
              data: this.sailingData.map((_, index) => ({
                x: baseline,
                y: index === 0
                  ? -1
                  : index === this.fuelData.length - 1
                    ? index + 1
                    : index
              })),
              borderColor: '#EE443F',
              borderWidth: 2,
              borderDash: [5, 5],
              pointRadius: 0,
              pointBorderWidth: 0,
              fill: false,
              parsing: false,
              spanGaps: true,
              pointHoverRadius: 0,
              pointHitRadius: 30
            },
            {
              data: this.sailingData.map((item) =>({
                key: item.owner_name,
                value: item.value
              })),
              borderWidth: 1,
              borderRadius: 5,
              barPercentage: 0.9,
              categoryPercentage: 0.6,
              backgroundColor: '#50CD89'
            }
          ]
        }
      }
      return null
    },
    fuelChartData() {
      if (this.fuelData) {
        if (this.fuelData.every((item) => item.value === 0)) {
          return null
        }

        const baseline = this.fuelData[0].target
        return {
          labels: this.fuelData.map((item) => item.owner_name),
          datasets: [
            {
              type: 'line',
              data: this.fuelData.map((_, index) => ({
                x: baseline,
                y: index === 0
                  ? -1
                  : index === this.fuelData.length - 1
                    ? index + 1
                    : index
              })),
              borderColor: '#EE443F',
              borderWidth: 2,
              borderDash: [5, 5],
              pointRadius: 0,
              pointBorderWidth: 0,
              fill: false,
              parsing: false,
              spanGaps: true,
              pointHoverRadius: 0,
              pointHitRadius: 30
            },
            {
              data: this.fuelData.map((item) =>({
                key: item.owner_name,
                value: item.value
              })),
              borderWidth: 1,
              borderRadius: 5,
              barPercentage: 0.9,
              categoryPercentage: 0.6,
              backgroundColor: '#AF4DDD'
            }
          ]
        }
      }
      return null
    },
    loadingTimeChartData() {
      if (this.loadingTimeData) {
        if (this.loadingTimeData.every((item) => item.value === 0 || item.value === null)) {
          return null
        }

        return {
          labels: this.loadingTimeData.map((item) => item.pol),
          datasets: [
            {
              label: 'plan',
              data: this.loadingTimeData.map((item) => item.target),
              backgroundColor: '#3E97FF'
            },
            {
              label: 'actual',
              data: this.loadingTimeData.map((item) => ({
                key: item.pol,
                value: item.value
              })),
              backgroundColor: '#076FE9'
            }
          ]
        }
      }
      return null
    },
    unloadingTimeChartData() {
      if (this.unloadingTimeData) {
        if (this.unloadingTimeData.every((item) => item.value === 0)) {
          return null
        }

        return {
          labels: this.unloadingTimeData.map((item) => item.pod),
          datasets: [
            {
              label: 'plan',
              data: this.unloadingTimeData.map((item) => item.target),
              backgroundColor: '#7239EA'
            },
            {
              label: 'actual',
              data: this.unloadingTimeData.map((item) => ({
                key: item.pod,
                value: item.value
              })),
              backgroundColor: '#4A00E6'
            }
          ]
        }
      }
      return null
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barThickness: 25,
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'value',
          yAxisKey: 'key'
        },
        scales: {
          x: {
            type: 'logarithmic',
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          y: {
            grid: {
              display: false,
              drawBorder: false
            },
            min: -0.5,
            ticks: {
              callback(value, index) {
                return `${index + 1}`;
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: tooltipConfig({
            mode: 'nearest',
            intersect: true,
            position: 'nearest',
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const { label, datasetIndex } = item
                if (datasetIndex === 0) {
                  return 'Target'
                }
                return label
              },
              label(tooltipItem) {
                const { value } = tooltipItem.raw
                if (tooltipItem.datasetIndex === 0) {
                  return `${handleValidateAndFormatNumber(tooltipItem?.raw?.x, 3)} days`
                }
                return `${handleValidateAndFormatNumber(value, 3)} days`
              }
            }
          })
        }
      }
    },
    stackedChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barThickness: 25,
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'value',
          yAxisKey: 'key'
        },
        barPercentage: 0.8,
        borderRadius: {
          topLeft: 5,
          topRight: 5,
          bottomLeft: 5,
          bottomRight: 5
        },
        layout: {
          padding: {
            left: 5
          }
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          y: {
            stacked: true,
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              callback(value, index) {
                return `${index + 1}`;
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const { label } = item
                return label
              },
              label(tooltipItem) {
                const chartData = this.chart?.data
                const datasets = chartData?.datasets
                const dataIndex = tooltipItem?.dataIndex

                const plan = datasets.find(({ label }) => label === 'plan')?.data?.[dataIndex]
                const actual = datasets.find(({ label }) => label === 'actual')?.data?.[dataIndex]?.value
                const loss = actual - plan || null

                return [
                  `Actual: ${handleValidateAndFormatNumber(actual, 3) || 'N/A'} days`,
                  `Plan: ${handleValidateAndFormatNumber(plan, 3) || 'N/A'} days`,
                  `Loss: ${handleValidateAndFormatNumber(loss, 3) || 'N/A'} days`
                ]
              }
            }
          })
        }
      }
    },
    chartPlugins() {
      return [
        this.drawBarValuePlugin(),
        this.drawBarLabelPlugin()
      ]
    },
    chartHeight() {
      const countHeight = (itemLength) => {
        const baseHeight = itemLength > 3 ? 75 : 60
        const barThickness = itemLength > 3 ? 30 : 20
        const padding = itemLength > 5 ? 60 : 10
        return Math.min(600, baseHeight + (itemLength * barThickness) + padding)
      }

      return {
        sailing: countHeight(this.sailingData.length),
        fuel: countHeight(this.fuelData.length),
        loadingTime: countHeight(this.loadingTimeData.length),
        unloadingTime: countHeight(this.unloadingTimeData.length)
      }
    }
  },
  methods: {
    drawBarValuePlugin() {
      return {
        id: 'drawBarValue',
        afterDatasetsDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(1);

          meta.data.forEach((arc, index) => {
            const dataObjIndex = data.datasets.findIndex((item) => item.data.find((itemData) => itemData?.hasOwnProperty('value')))
            const value = data.datasets[dataObjIndex].data[index].value;
            const formattedValue = handleValidateAndFormatNumber(value, 3)

            const {x, y, width} = arc;

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = 'normal 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(formattedValue, x - width + 25, y);
            ctx.restore();
          });
        }
      }
    },
    drawBarLabelPlugin() {
      return {
        id: 'drawBarLabel',
        afterDatasetsDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(1);

          meta.data.forEach((arc, index) => {
            const dataObjIndex = data.datasets.findIndex((item) => item.data.find((itemData) => itemData?.hasOwnProperty('key')))
            const value = data.datasets[dataObjIndex].data[index].key;
            const {x, y, width, height, base} = arc

            ctx.save();
            ctx.fillStyle = '#000';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(value, base, y + height / 2 + 7);
            ctx.restore();
          });
        }
      }
    }
  }
}
</script>

<style lang="scss"  scoped>
</style>